import { Link as RouterLink } from "react-router-dom";
import { Box, Button, Paper, Typography } from "@mui/material";
import {
  ArrowForward as ArrowForwardIcon,
  Radar as RadarIcon,
} from "@mui/icons-material";
import { Clamp } from "../clamp/clamp";
import { Flex } from "../Flex";
import { FindingsDailyCountChart } from "../FindingsDailyCountChart/FindingsDailyCountChart";
import { numberKFormat } from "~/lib/number-k-formatting";
import { SpaceSummaryAssetGroupButtons } from "./SpaceSummaryAssetGroupButtons";
import { ViewType } from "../view-switcher";
import { SpaceSummaryData, useSpaceSummary } from "./useSpaceSummary";
import { FleetIcon } from "~/icons";
import { PriorityFindingsDeltaPercentage } from "~/pages/space/home-dashboard/PriorityFindingsCard/components/PriorityFindingsDeltaPercentage";

export type SpaceSummaryCardProps = {
  summaryData: SpaceSummaryData;
};

export function SpaceSummaryCard({ summaryData }: SpaceSummaryCardProps) {
  const summary = useSpaceSummary({ summaryData });
  const { mrn, name, assetGroupStats, description } = summary;
  const { dailyAssetsCounts, dailyPriorityFindingsCounts } = summary;
  const { overviewHref } = summary;
  const { metricsEntries } = summaryData;
  const isWorkspace = Boolean(summaryData.workspaceId);

  const noDescriptionText = `No description provided for this ${isWorkspace ? "workspace" : "space"}`;
  const descriptionIsEmpty = !Boolean(description);
  const descriptionText = descriptionIsEmpty ? noDescriptionText : description;

  const statItems = [
    {
      icon: FleetIcon,
      label: "Asset count",
      value: dailyAssetsCounts.newestCount,
      deltaCount: dailyAssetsCounts.countDelta,
      deltaPercentage: dailyAssetsCounts.countDeltaPercentage,
      deltaColor: "secondary",
    },
    {
      icon: RadarIcon,
      label: "Priority findings",
      value: dailyPriorityFindingsCounts.newestCount,
      deltaCount: dailyPriorityFindingsCounts.countDelta,
      deltaPercentage: dailyPriorityFindingsCounts.countDeltaPercentage,
      deltaColor: "primary",
    },
  ] as const;

  return (
    <Box
      className="space-summary-card"
      component={Paper}
      sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}
    >
      <Button
        className="header"
        component={RouterLink}
        to={overviewHref}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 1,
          m: -1,
          textTransform: "none",
        }}
      >
        <Typography fontWeight={700}>{name}</Typography>
        <ArrowForwardIcon sx={{ color: "primary.main" }} />
      </Button>

      <Box
        className="description"
        sx={{
          display: "flex",
          alignItems: descriptionIsEmpty ? "center" : "flex-start",
          justifyContent: descriptionIsEmpty ? "center" : "flex-start",
          minHeight: (theme) => theme.spacing(6),
        }}
      >
        <Clamp lines={2}>
          <Typography
            sx={{
              color: "text.secondary",
              opacity: descriptionIsEmpty ? 0.5 : 1,
            }}
          >
            {descriptionText}
          </Typography>
        </Clamp>
      </Box>

      <Flex className="group-summaries" sx={{ flexDirection: "column" }}>
        <SpaceSummaryAssetGroupButtons
          viewType={ViewType.Cards}
          assetGroupStats={assetGroupStats}
        />
      </Flex>

      <Flex className="stat-summaries" sx={{ flexDirection: "column", gap: 1 }}>
        {statItems.map((item) => (
          <Flex
            key={item.label}
            className="stat-item"
            sx={{ alignItems: "center", gap: 0.5 }}
          >
            <item.icon
              fontSize="inherit"
              sx={{ color: "text.primary", fontSize: 18 }}
            />
            <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
              {item.label}
            </Typography>
            <Flex ml="auto" gap={1} alignItems="center">
              <Typography sx={{ fontWeight: 700 }}>
                {numberKFormat.format(item.value)}
              </Typography>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: 12,
                  color: "text.secondary",
                }}
              >
                ({item.deltaCount})
              </Typography>
              <PriorityFindingsDeltaPercentage
                deltaCount={item.deltaCount}
                deltaPercentage={item.deltaPercentage}
                color={item.deltaColor}
                small
                sx={{ minWidth: 64 }}
              />
            </Flex>
          </Flex>
        ))}
      </Flex>

      <Box className="stat-chart">
        <FindingsDailyCountChart entityMrn={mrn} entries={metricsEntries} />
      </Box>
    </Box>
  );
}
